import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MetricsCard from './components/MetricsCard';
import FilterBar from './components/FilterBar';
import CaseStudyCard from './components/CaseStudyCard';
import TestimonialCard from './components/TestimonialCard';
import CertificationBadge from './components/CertificationBadge';
import IndustryRecognition from './components/IndustryRecognition';
import ClientLogoGrid from './components/ClientLogoGrid';
import ROICalculator from './components/ROICalculator';

const SuccessStoriesShowcase = () => {
  const [filters, setFilters] = useState({
    industry: 'all',
    service: 'all',
    size: 'all',
    region: 'all',
    search: ''
  });

  const [filteredCaseStudies, setFilteredCaseStudies] = useState([]);
  const [activeTab, setActiveTab] = useState('case-studies');

  const metricsData = [
    {
      icon: "Briefcase",
      value: "880+",
      label: "Projects Completed",
      trend: "up",
      trendValue: "12%",
      color: "primary"
    },
    {
      icon: "Users",
      value: "678+",
      label: "Organizations Served",
      trend: "up",
      trendValue: "8%",
      color: "success"
    },
    {
      icon: "Award",
      value: "112",
      label: "Industry Recognitions",
      trend: "up",
      trendValue: "15%",
      color: "accent"
    },
    {
      icon: "TrendingUp",
      value: "98.5%",
      label: "Client Satisfaction",
      trend: "up",
      trendValue: "2%",
      color: "secondary"
    }];


  const caseStudiesData = [
    {
      id: 1,
      company: "TechVision Industries",
      location: "Mumbai, Maharashtra",
      industry: "Information Technology",
      service: "HR Solutions",
      size: "large",
      region: "west",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e83fa0dc-1764772276072.png",
      imageAlt: "Modern office space with employees collaborating at workstations with computers and natural lighting",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15cb0303c-1764656703640.png",
      logoAlt: "Professional technology company logo with blue and white color scheme on clean background",
      challenge: "Struggling with high employee turnover rate of 35% annually and inefficient recruitment processes causing project delays and increased costs.",
      solution: "Implemented comprehensive HR transformation including talent acquisition strategy, employee engagement programs, performance management system, and retention initiatives with data-driven insights.",
      results: [
        { icon: "TrendingDown", value: "18%", label: "Turnover Reduced" },
        { icon: "Clock", value: "40%", label: "Faster Hiring" },
        { icon: "DollarSign", value: "₹2.5Cr", label: "Cost Savings" },
        { icon: "Users", value: "95%", label: "Satisfaction" }],

      timeline: "January 2024 - September 2024 (9 months)"
    },
    {
      id: 2,
      company: "GlobalTrade Enterprises",
      location: "Delhi NCR",
      industry: "Retail & E-commerce",
      service: "Global Trade",
      size: "enterprise",
      region: "north",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_110d00d0b-1765171426065.png",
      imageAlt: "International shipping containers stacked at busy port terminal with cranes loading cargo ships",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_122f81d03-1764691404571.png",
      logoAlt: "Global trade company logo featuring world map icon with green and blue corporate colors",
      challenge: "Facing difficulties in establishing reliable international supplier networks and managing complex import-export documentation leading to shipment delays.",
      solution: "Established comprehensive global trade network with vetted suppliers across 15 countries, implemented automated documentation system, and provided trade compliance consulting.",
      results: [
        { icon: "Globe", value: "15", label: "New Markets" },
        { icon: "TrendingUp", value: "60%", label: "Trade Volume" },
        { icon: "Clock", value: "50%", label: "Faster Delivery" },
        { icon: "Shield", value: "100%", label: "Compliance" }],

      timeline: "March 2024 - November 2024 (8 months)"
    },
    {
      id: 3,
      company: "MediCare Solutions",
      location: "Bangalore, Karnataka",
      industry: "Healthcare",
      service: "Recruitment",
      size: "medium",
      region: "south",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d85f07f-1764675861970.png",
      imageAlt: "Healthcare professionals in white coats consulting with patient in modern hospital examination room",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1aaa7878f-1764674639799.png",
      logoAlt: "Healthcare company logo with medical cross symbol in red and white professional design",
      challenge: "Critical shortage of specialized medical staff including nurses, technicians, and administrative personnel affecting patient care quality and hospital operations.",
      solution: "Deployed targeted healthcare recruitment campaign with specialized screening processes, credential verification, and onboarding support for 150+ medical professionals.",
      results: [
        { icon: "Users", value: "150+", label: "Staff Hired" },
        { icon: "Clock", value: "30%", label: "Faster Filling" },
        { icon: "Heart", value: "92%", label: "Patient Satisfaction" },
        { icon: "Award", value: "98%", label: "Retention Rate" }],

      timeline: "February 2024 - August 2024 (6 months)"
    },
    {
      id: 4,
      company: "BuildRight Construction",
      location: "Pune, Maharashtra",
      industry: "Construction",
      service: "Engineering Services",
      size: "large",
      region: "west",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1615a8a81-1765823500099.png",
      imageAlt: "Construction site with engineers reviewing blueprints near steel framework of modern building under development",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5a66882-1765275628276.png",
      logoAlt: "Construction company logo featuring building silhouette with orange and gray industrial color palette",
      challenge: "Needed specialized engineering expertise for complex infrastructure project including structural design, quality assurance, and project management support.",
      solution: "Provided team of 25 experienced engineers including structural, civil, and quality control specialists with project management oversight and technical consulting.",
      results: [
        { icon: "CheckCircle2", value: "100%", label: "On Schedule" },
        { icon: "DollarSign", value: "₹5Cr", label: "Under Budget" },
        { icon: "Shield", value: "Zero", label: "Safety Incidents" },
        { icon: "Award", value: "A+", label: "Quality Rating" }],

      timeline: "April 2024 - December 2024 (8 months)"
    },
    {
      id: 5,
      company: "FinanceFirst Bank",
      location: "Hyderabad, Telangana",
      industry: "Finance & Banking",
      service: "HR Solutions",
      size: "enterprise",
      region: "south",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_169b73ffb-1764669848355.png",
      imageAlt: "Professional banking environment with financial advisors assisting customers at modern service counters",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18c4d5fec-1764779131733.png",
      logoAlt: "Banking institution logo with shield and pillar symbols in navy blue and gold professional design",
      challenge: "Expanding operations required hiring 200+ banking professionals across multiple branches while maintaining service quality and regulatory compliance.",
      solution: "Executed large-scale recruitment drive with compliance-focused screening, comprehensive training programs, and performance management system implementation.",
      results: [
        { icon: "Users", value: "200+", label: "Professionals" },
        { icon: "MapPin", value: "15", label: "New Branches" },
        { icon: "TrendingUp", value: "85%", label: "Productivity" },
        { icon: "Shield", value: "100%", label: "Compliance" }],

      timeline: "January 2024 - October 2024 (9 months)"
    },
    {
      id: 6,
      company: "LogiMove Supply Chain",
      location: "Chennai, Tamil Nadu",
      industry: "Logistics & Supply Chain",
      service: "Global Trade",
      size: "medium",
      region: "south",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_135ea089a-1764637482598.png",
      imageAlt: "Warehouse facility with organized inventory shelves and workers operating forklifts in modern logistics center",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0dc8164-1764793918043.png",
      logoAlt: "Logistics company logo with arrow and package symbols in red and black dynamic design",
      challenge: "Needed to optimize international shipping routes and establish partnerships with reliable freight forwarders to reduce costs and improve delivery times.",
      solution: "Developed optimized shipping network with strategic partnerships, implemented tracking systems, and provided customs clearance support across major trade routes.",
      results: [
        { icon: "TrendingDown", value: "35%", label: "Cost Reduction" },
        { icon: "Clock", value: "45%", label: "Faster Delivery" },
        { icon: "Globe", value: "20", label: "Trade Routes" },
        { icon: "CheckCircle2", value: "99.5%", label: "On-Time Rate" }],

      timeline: "May 2024 - November 2024 (6 months)"
    },
    {
      id: 7,
      company: "ManufacturePro Industries",
      location: "Ahmedabad, Gujarat",
      industry: "Manufacturing",
      service: "Engineering Services",
      size: "large",
      region: "west",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_145099d0a-1764662673849.png",
      imageAlt: "Industrial manufacturing floor with automated machinery and engineers monitoring production processes",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5eaf036-1764674640769.png",
      logoAlt: "Manufacturing company logo with gear and factory symbols in steel gray and blue industrial colors",
      challenge: "Required process optimization and quality improvement initiatives to meet international standards and increase production efficiency.",
      solution: "Deployed engineering team for process analysis, implemented lean manufacturing principles, quality control systems, and provided technical training to workforce.",
      results: [
        { icon: "TrendingUp", value: "55%", label: "Efficiency Gain" },
        { icon: "Award", value: "ISO 9001", label: "Certified" },
        { icon: "TrendingDown", value: "40%", label: "Defect Rate" },
        { icon: "DollarSign", value: "₹3.8Cr", label: "Annual Savings" }],

      timeline: "February 2024 - September 2024 (7 months)"
    },
    {
      id: 8,
      company: "RetailHub Ventures",
      location: "Kolkata, West Bengal",
      industry: "Retail & E-commerce",
      service: "Recruitment",
      size: "medium",
      region: "east",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_153d65b95-1765053106122.png",
      imageAlt: "Modern retail store interior with customers shopping and staff assisting at checkout counters",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_156ebb0ce-1764644182024.png",
      logoAlt: "Retail company logo with shopping bag icon in vibrant purple and orange contemporary design",
      challenge: "Rapid expansion required hiring 100+ retail staff across multiple locations with high customer service standards and product knowledge.",
      solution: "Implemented comprehensive recruitment and training program with customer service focus, performance tracking, and retention strategies.",
      results: [
        { icon: "Users", value: "100+", label: "Staff Hired" },
        { icon: "MapPin", value: "8", label: "New Stores" },
        { icon: "Heart", value: "90%", label: "Customer Rating" },
        { icon: "TrendingUp", value: "88%", label: "Retention" }],

      timeline: "March 2024 - October 2024 (7 months)"
    }];


  const testimonialsData = [
    {
      id: 1,
      name: "Rajesh Kumar",
      position: "Chief Human Resources Officer",
      company: "TechVision Industries",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_124ed0d25-1763296291208.png",
      avatarAlt: "Professional headshot of Indian male executive in navy blue suit with warm smile in corporate office",
      content: "InChi Solutions Pvt Ltd transformed our HR operations completely. Their strategic approach to talent acquisition and retention helped us reduce turnover by 18% while improving employee satisfaction. The team's expertise and dedication made them true partners in our growth journey.",
      rating: 5,
      date: "November 2024"
    },
    {
      id: 2,
      name: "Priya Sharma",
      position: "Director of Operations",
      company: "GlobalTrade Enterprises",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19dcb987c-1763301304743.png",
      avatarAlt: "Professional headshot of Indian female executive in burgundy blazer with confident expression in modern office",
      content: "Working with InChi Solutions Pvt Ltd on our global trade expansion was exceptional. They established reliable supplier networks across 15 countries and streamlined our documentation processes. Our trade volume increased by 60% while maintaining 100% compliance.",
      rating: 5,
      date: "November 2024"
    },
    {
      id: 3,
      name: "Dr. Amit Patel",
      position: "Hospital Administrator",
      company: "MediCare Solutions",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_126a6b0dc-1763300720152.png",
      avatarAlt: "Professional headshot of Indian male doctor in white coat with stethoscope in hospital setting",
      content: "InChi Solutions Pvt Ltd solved our critical staffing shortage by recruiting 150+ qualified medical professionals. Their specialized healthcare recruitment process and credential verification ensured we hired the right talent, improving our patient satisfaction to 92%.",
      rating: 5,
      date: "October 2024"
    },
    {
      id: 4,
      name: "Vikram Singh",
      position: "Project Director",
      company: "BuildRight Construction",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1dedfe473-1763295924083.png",
      avatarAlt: "Professional headshot of Indian male engineer in yellow safety vest at construction site with hard hat",
      content: "The engineering team provided by InChi Solutions Pvt Ltd was instrumental in completing our complex infrastructure project on schedule and under budget. Their technical expertise and project management support exceeded our expectations with zero safety incidents.",
      rating: 5,
      date: "December 2024"
    },
    {
      id: 5,
      name: "Meera Reddy",
      position: "Head of Human Capital",
      company: "FinanceFirst Bank",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b64e93de-1763299173502.png",
      avatarAlt: "Professional headshot of Indian female banking executive in formal black suit with pearl necklace",
      content: "InChi Solutions Pvt Ltd executed our large-scale recruitment drive flawlessly, hiring 200+ banking professionals across 15 branches. Their compliance-focused approach and comprehensive training programs ensured smooth expansion while maintaining service quality.",
      rating: 5,
      date: "October 2024"
    },
    {
      id: 6,
      name: "Arjun Nair",
      position: "Supply Chain Manager",
      company: "LogiMove Supply Chain",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa51fe75-1763293605343.png",
      avatarAlt: "Professional headshot of Indian male logistics manager in blue shirt at warehouse facility",
      content: "InChi Solutions Pvt Ltd optimized our international shipping operations, reducing costs by 35% and improving delivery times by 45%. Their strategic partnerships and tracking systems have transformed our logistics efficiency with 99.5% on-time delivery.",
      rating: 5,
      date: "November 2024"
    }];


  const certificationsData = [
    {
      name: "ISO 9001:2015",
      issuer: "International Organization for Standardization",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10be834c4-1764657247266.png",
      logoAlt: "ISO 9001 certification badge with blue and white quality management system symbol",
      year: "2024"
    },
    {
      name: "MSME Registered",
      issuer: "Ministry of MSME, Government of India",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fae65811-1764830396492.png",
      logoAlt: "MSME registration certificate logo with Indian government emblem in orange and green",
      year: "2023"
    },
    {
      name: "SHRM Certified",
      issuer: "Society for Human Resource Management",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1517dd8a7-1765296275564.png",
      logoAlt: "SHRM professional certification badge with blue and white HR excellence symbol",
      year: "2024"
    },
    {
      name: "Trade Excellence Award",
      issuer: "Federation of Indian Export Organisations",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_198c79d5a-1764657847897.png",
      logoAlt: "Trade excellence award trophy logo with gold and red international commerce symbol",
      year: "2024"
    }];


  const recognitionsData = [
    {
      icon: "Award",
      title: "Best HR Solutions Provider 2024",
      description: "Recognized for excellence in talent acquisition and employee engagement strategies across multiple industries.",
      year: "2024",
      organization: "HR Excellence Awards India"
    },
    {
      icon: "TrendingUp",
      title: "Top 10 Recruitment Firms",
      description: "Ranked among India\'s leading recruitment agencies for consistent delivery and client satisfaction.",
      year: "2024",
      organization: "Business Today Magazine"
    },
    {
      icon: "Globe",
      title: "International Trade Facilitator",
      description: "Honored for establishing robust global trade networks and supporting Indian businesses in international markets.",
      year: "2024",
      organization: "Export Promotion Council"
    },
    {
      icon: "Users",
      title: "Employer of Choice",
      description: "Recognized for creating exceptional workplace culture and employee development programs.",
      year: "2024",
      organization: "Great Place to Work Institute"
    }];


  const clientLogosData = [
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_154f20177-1764661576649.png",
      logoAlt: "Technology company logo with modern blue and white design representing innovation"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5eaf036-1764674640769.png",
      logoAlt: "Manufacturing company logo with industrial gray and orange color scheme"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1aaa7878f-1764674639799.png",
      logoAlt: "Healthcare organization logo with medical cross in red and white professional design"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17460234e-1764670828884.png",
      logoAlt: "Financial services company logo with gold and navy blue corporate colors"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_156ebb0ce-1764644182024.png",
      logoAlt: "Retail brand logo with vibrant purple and orange contemporary design"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1077df53a-1764669637707.png",
      logoAlt: "Construction company logo with building silhouette in gray and yellow"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0dc8164-1764793918043.png",
      logoAlt: "Logistics company logo with arrow and package symbols in red and black"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_168249a27-1764668836186.png",
      logoAlt: "E-commerce platform logo with shopping cart icon in green and blue"
    }];


  useEffect(() => {
    filterCaseStudies();
  }, [filters]);

  const filterCaseStudies = () => {
    let filtered = [...caseStudiesData];

    if (filters?.industry !== 'all') {
      filtered = filtered?.filter((cs) =>
        cs?.industry?.toLowerCase()?.includes(filters?.industry?.toLowerCase())
      );
    }

    if (filters?.service !== 'all') {
      filtered = filtered?.filter((cs) =>
        cs?.service?.toLowerCase()?.includes(filters?.service?.toLowerCase())
      );
    }

    if (filters?.size !== 'all') {
      filtered = filtered?.filter((cs) => cs?.size === filters?.size);
    }

    if (filters?.region !== 'all') {
      filtered = filtered?.filter((cs) => cs?.region === filters?.region);
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter((cs) =>
        cs?.company?.toLowerCase()?.includes(searchLower) ||
        cs?.challenge?.toLowerCase()?.includes(searchLower) ||
        cs?.solution?.toLowerCase()?.includes(searchLower)
      );
    }

    setFilteredCaseStudies(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      industry: 'all',
      service: 'all',
      size: 'all',
      region: 'all',
      search: ''
    });
  };

  const handleViewDetails = (caseStudy) => {
    console.log('View details for:', caseStudy);
  };

  const tabs = [
    { id: 'case-studies', label: 'Case Studies', icon: 'Briefcase' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' },
    { id: 'recognitions', label: 'Recognitions', icon: 'Award' }];


  return (
    <>
      <Helmet>
        <title>Success Stories & Case Studies | InChi Solutions Hub</title>
        <meta name="description" content="Explore 880+ successful projects, 678+ client testimonials, and 112 industry recognitions. Discover how InChi Solutions Pvt Ltd transforms businesses through HR, trade, and engineering excellence." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-1 pt-16">
          <section className="bg-gradient-to-br from-primary to-secondary text-white py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                  Success Stories & Achievements
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                  Discover how we've helped 678+ organizations achieve their goals through 880+ successful projects
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {metricsData?.map((metric, index) =>
                  <MetricsCard key={index} {...metric} />
                )}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                <div className="flex-1">
                  <FilterBar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    onReset={handleResetFilters} />


                  <div className="mt-6 bg-card rounded-lg shadow-sm">
                    <div className="border-b border-border overflow-x-auto">
                      <div className="flex gap-1 p-2 min-w-max">
                        {tabs?.map((tab) =>
                          <button
                            key={tab?.id}
                            onClick={() => setActiveTab(tab?.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${activeTab === tab?.id ?
                                'bg-primary text-primary-foreground' :
                                'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                            }>

                            <Icon name={tab?.icon} size={18} />
                            <span>{tab?.label}</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-4 md:p-6">
                      {activeTab === 'case-studies' &&
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                              Showing {filteredCaseStudies?.length} of {caseStudiesData?.length} case studies
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            {filteredCaseStudies?.map((caseStudy) =>
                              <CaseStudyCard
                                key={caseStudy?.id}
                                caseStudy={caseStudy}
                                onViewDetails={handleViewDetails} />

                            )}
                          </div>

                          {filteredCaseStudies?.length === 0 &&
                            <div className="text-center py-12">
                              <Icon name="Search" size={48} className="text-muted mx-auto mb-4" />
                              <h3 className="text-lg font-semibold text-foreground mb-2">
                                No case studies found
                              </h3>
                              <p className="text-sm text-muted-foreground mb-4">
                                Try adjusting your filters or search terms
                              </p>
                              <Button variant="outline" onClick={handleResetFilters}>
                                Reset Filters
                              </Button>
                            </div>
                          }
                        </div>
                      }

                      {activeTab === 'testimonials' &&
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {testimonialsData?.map((testimonial) =>
                            <TestimonialCard key={testimonial?.id} testimonial={testimonial} />
                          )}
                        </div>
                      }

                      {activeTab === 'recognitions' &&
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                              Industry Recognitions
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                              {recognitionsData?.map((recognition, index) =>
                                <IndustryRecognition key={index} recognition={recognition} />
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                              Certifications & Accreditations
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                              {certificationsData?.map((certification, index) =>
                                <CertificationBadge key={index} certification={certification} />
                              )}
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <div className="lg:w-80 space-y-6">
                  <ROICalculator />

                  <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Icon name="Download" size={20} className="text-accent" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground">
                        Download Resources
                      </h3>
                    </div>
                    <div className="space-y-3">
                      <Button variant="outline" fullWidth iconName="FileText" iconPosition="left">
                        Case Study PDFs
                      </Button>
                      <Button variant="outline" fullWidth iconName="BarChart3" iconPosition="left">
                        Success Metrics Report
                      </Button>
                      <Button variant="outline" fullWidth iconName="Award" iconPosition="left">
                        Certifications
                      </Button>
                    </div>
                  </div>

                  <div className="bg-primary text-primary-foreground rounded-lg p-4 md:p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3">Ready to Start Your Success Story?</h3>
                    <p className="text-sm mb-4 opacity-90">
                      Join 678+ organizations that trust InChi Solutions Pvt Ltd for their business transformation.
                    </p>
                    <Button variant="secondary" fullWidth iconName="ArrowRight" iconPosition="right">
                      Get Started
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16 bg-muted">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <ClientLogoGrid clients={clientLogosData} />
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-2xl p-6 md:p-8 lg:p-12">
                <div className="max-w-3xl mx-auto text-center">
                  <Icon name="Sparkles" size={48} className="mx-auto mb-4" />
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                    Let's Create Your Success Story
                  </h2>
                  <p className="text-lg md:text-xl mb-6 opacity-90">
                    Transform your business challenges into achievements with InChi Solutions Pvt Ltd's proven expertise
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button variant="secondary" size="lg" iconName="Calendar" iconPosition="left">
                      Schedule Consultation
                    </Button>
                    <Button variant="outline" size="lg" className="bg-white/10 hover:bg-white/20 text-white border-white">
                      View All Services
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>);

};

export default SuccessStoriesShowcase;